import { Injectable } from '@angular/core';
// Firebase App (the core Firebase SDK) is always required and
// must be listed before other Firebase SDKs
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { AnalyticsDoubleTrackProperties } from 'src/app/core/services/analytics/models/analytics-double-track-properties.model';
import { AnalyticsEventProperties } from 'src/app/core/services/analytics/models/analytics-event-properties.model';
import { ActionEventParams } from 'src/app/core/services/analytics/models/firebase-action-event-params.model';
import { AccountService } from 'src/app/shared/services/account.service';

@Injectable({ providedIn: 'root' })
export class FirebaseAnalyticsService {
  constructor(
    protected firebase: FirebaseX,
    private accountService: AccountService,
  ) {
    // Enables analytics data collection
    firebase.setAnalyticsCollectionEnabled(true);
  }

  private logEvent(event: string, data: Record<string, any>) {
    if (event.split(' ').length > 1) {
      console.error("invalid event name - event name can't have spaces");
    }
    this.firebase.logEvent(event, { ...data });
  }

  /**
   * Used for logging user actions like button clicks.
   *
   * @param eventName
   * @param eventParams
   */
  logClick(
    eventName: string,
    eventParams: ActionEventParams | AnalyticsEventProperties | AnalyticsDoubleTrackProperties,
  ) {
    if (eventName.split(' ').length > 1) {
      console.error("invalid event name - event name can't have spaces");
    }
    this.setUserProperties(eventParams, eventName);

    this.logEvent(eventName, eventParams);
  }

  /**
   * Log page event - Mostly used with route change.
   *
   * @param pagename - GA page name.
   */
  logPageEvent(pagename: string) {
    this.logEvent('page_view', {
      event_label: pagename,
      page_title: pagename,
      screen_view: pagename,
    });
    this.firebase.setScreenName(pagename);
  }

  /**
   * Used to log modal page views.
   *
   * @param screenName - The modal page name
   */
  logScreenEvent(screenName: string) {
    this.logEvent('screen_view', {
      screen_name: screenName,
    });
  }

  /**
   * Sets user properties in Firebase Analytics based on certain conditions.
   *
   * @param eventParams - An object containing event parameters.
   * @param eventName - An optional event name.
   */
  setUserProperties(eventParams, eventName: string) {
    if (
      eventParams.application_type === 'worker' &&
      eventParams.application_step === 2 &&
      eventName === 'application_progress'
    ) {
      this.firebase.setUserProperty('user_location', eventParams.location);
      this.firebase.setUserProperty('user_hours', eventParams.hours);
    }

    if (eventName === 'application_complete') {
      this.firebase.setUserProperty('location', eventParams.location);
      this.firebase.setUserProperty('member_type', eventParams.application_type);
      if (eventParams.application_type === 'client') {
        this.firebase.setUserProperty('user_ndis', eventParams.ndis_funding);
        this.firebase.setUserProperty('user_support_type', eventParams.support_type);
      }
      if (eventParams.application_type === 'worker') {
        this.firebase.setUserProperty('user_hours', eventParams.hours);
      }
    }

    if (eventName === 'login') {
      const currentUser = this.getCurrentUserProperties();
      this.firebase.setUserProperty('member_type', currentUser.application_type);
      this.firebase.setUserId(currentUser.user_id);
    }
  }

  // Create current user properties to inject user_id and application_type.
  private getCurrentUserProperties(): AnalyticsEventProperties {
    const currentUser = this.accountService.getUser();
    // Account type mapping object
    const accountTypeMapping: {
      [key: string]: string;
    } = {
      Carer: 'worker',
      Consumer: 'client',
    };

    // If the user is logged in, return application_type and user_id properties.
    if (currentUser) {
      const accountTypeMappingResult = accountTypeMapping[currentUser.custom?.account_type];
      return {
        application_type: accountTypeMappingResult || currentUser.custom?.account_type?.toLowerCase(),
        user_id: currentUser.custom.user_uid,
      };
    }
    return {};
  }
}
